import React from "react";
import { useNavigate } from "react-router-dom";
import { Search, FileX2, ChevronRight } from "lucide-react";

export const GradientPanel = ({ children, className = "" }) => (
  <div className={`gr-ring fade-up ${className}`}>
    <div className="gr-inner p-6 md:p-7">{children}</div>
  </div>
);

export const Breadcrumb = ({ items }) => {
  const navigate = useNavigate();
  return (
    <div className="flex items-center gap-2 text-sm mb-4">
      {items.map((it, i) => (
        <span key={i} className="flex items-center gap-2">
          {it.path ? (
            <button onClick={() => navigate(it.path)} className="text-blue-600 hover:underline font-medium">
              {it.label}
            </button>
          ) : (
            <span className="text-gray-500">{it.label}</span>
          )}
          {i < items.length - 1 && <span className="text-gray-300">/</span>}
        </span>
      ))}
    </div>
  );
};

export const PageTitle = ({ title, subtitle, children }) => (
  <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
    <div>
      <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
      {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
    </div>
    {children}
  </div>
);

export const SearchInput = ({ placeholder = "Type to filter results", value, onChange, className = "" }) => (
  <div className={`relative ${className}`}>
    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
    <input
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-200 bg-white outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition"
    />
  </div>
);

export const EmptyState = ({ icon: Icon = FileX2, title, desc, action }) => (
  <div className="flex flex-col items-center justify-center text-center py-14 px-6">
    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 flex items-center justify-center mb-4">
      <Icon size={28} className="text-blue-400" strokeWidth={1.6} />
    </div>
    <h3 className="text-base font-semibold text-gray-800">{title}</h3>
    {desc && <p className="text-sm text-gray-500 mt-2 max-w-sm">{desc}</p>}
    {action && <div className="mt-5">{action}</div>}
  </div>
);

export const DataTable = ({ columns, rows = [] }) => (
  <div className="mt-4 overflow-x-auto">
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-gray-100">
          {columns.map((c) => (
            <th key={c} className="text-left font-semibold text-gray-500 py-3 px-3 whitespace-nowrap">{c}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr>
            <td colSpan={columns.length} className="py-14">
              <div className="flex flex-col items-center justify-center text-gray-400">
                <FileX2 size={30} strokeWidth={1.4} className="mb-2" />
                <span className="text-sm font-semibold text-gray-600">No data found</span>
              </div>
            </td>
          </tr>
        ) : (
          rows.map((r, i) => (
            <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
              {columns.map((c) => (
                <td key={c} className="py-3 px-3 text-gray-700 whitespace-nowrap">{r[c] ?? "-"}</td>
              ))}
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

export const PrimaryButton = ({ children, onClick, className = "", icon: Icon }) => (
  <button
    onClick={onClick}
    className={`inline-flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors ${className}`}
  >
    {Icon && <Icon size={16} />}
    {children}
  </button>
);

export const Chevron = () => <ChevronRight size={16} className="text-gray-400" />;
